import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
    Button,
    FormControl,
    FormControlLabel,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import { useUser } from "../../contexts/UserContext";
import Loading from "../components/Loading";
import { FormatToBrl } from "../../utils/FormatToBrl";
import { MascaraCartaoCredito } from "../../utils/MascaraCartaoCredito";
import { MascaraDataCartaoCredito } from "../../utils/MascaraDataCartaoCredito";
import { TirarMascara } from "../../utils/TirarMascara";
import AlertError from "../components/AlertError";
import AlertSuccess from "../components/AlertSuccess";

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "white",
        borderRadius: "10px",
        "&.Mui-focused fieldset": {
            borderColor: "#0b243d",
        },
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#0b243d",
    },
};

const Checkout = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    useEffect(() => {
        if (!token) {
            navigate("/cadastro" /* Página de login ou cadastro */);
        }
    }, [navigate, token]);
    const [isMobile, setIsMobile] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("BOLETO");
    const [discountCode, setDiscountCode] = useState("");
    const [discountApplied, setDiscountApplied] = useState(false);
    // const [isChecked, setIsChecked] = useState(false);
    const [showBack, setShowBack] = useState(false);
    const [openSnackbarError, setOpenSnackbarError] = useState(false);
    const [openSnackbarSuccess, setOpenSnackbarSuccess] = useState(false);
    const [mensagem, setMensagem] = useState("");
    const [cardData, setCardData] = useState({
        name: "",
        number: "",
        expiry: "",
        cvv: ""
    });
    const [cardErrors, setCardErrors] = useState({});

    const [open, setOpen] = useState(false);

    const { fetchUser } = useUser();

    const plano = JSON.parse(localStorage.getItem("planoSelecionado"));

    useEffect(() => {
        if (!plano) {
            navigate("/Planos");
        }
    }, [navigate, plano]);


    const [cartItems] = useState(() => {
        if (plano) {
            return [plano];
        }
        return [];
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [])

    useEffect(() => {
        const verifyPlan = async () => {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/hasPlan`, {
                headers: {
                    Authorization: token
                }
            });
            if (response.data.status === 200) {
                navigate("../Dashboard");
            }
        }
        verifyPlan()
    }, [navigate, token])

    const originalTotal = cartItems.reduce((sum, item) => sum + item.price, 0);
    const total = discountApplied ? originalTotal - (originalTotal * (Number(discountApplied.valorDesconto) / 100)) : originalTotal;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handlePaymentChange = (event) => {
        setPaymentMethod(event.target.value);
    };

    const validateCardData = (data) => {
        const errors = { valid: true };

        // Nome no cartão (mínimo 3 caracteres, apenas letras e espaços)
        if (!data.name.trim()) {
            errors.name = "O nome no cartão é obrigatório";
            errors.valid = false;
        } else if (!/^[a-zA-ZÀ-ÿ\s]{3,}$/.test(data.name)) {
            errors.name = "Nome inválido";
            errors.valid = false;
        }

        // Número do cartão (somente números, entre 13 e 19 dígitos)
        const cleanNumber = data.number.replace(/\s+/g, "");
        if (!cleanNumber) {
            errors.number = "O número do cartão é obrigatório";
            errors.valid = false;
        } else if (!/^\d{13,19}$/.test(cleanNumber)) {
            errors.number = "Número de cartão inválido";
            errors.valid = false;
        }

        // Validade (MM/YYYY)
        if (!data.expiry) {
            errors.expiry = "A data de validade é obrigatória";
            errors.valid = false;
        } else if (!/^(0[1-9]|1[0-2])\/\d{4}$/.test(data.expiry)) {
            errors.expiry = "Formato inválido (MM/AAAA)";
            errors.valid = false;
        } else {
            const [month, year] = data.expiry.split("/");
            const now = new Date();
            const expiryDate = new Date(year, month); // mês/ano do cartão
            if (expiryDate < now) {
                errors.expiry = "Cartão vencido";
                errors.valid = false;
            }
        }

        // CVV (3 ou 4 dígitos)
        if (!data.cvv) {
            errors.cvv = "O CVV é obrigatório";
            errors.valid = false;
        } else if (!/^\d{3,4}$/.test(data.cvv)) {
            errors.cvv = "CVV inválido";
            errors.valid = false;
        }

        return errors;
    };


    const handleFinishOrder = async () => {
        let errors = { valid: true };
        if (paymentMethod === "CREDIT_CARD") {
            errors = validateCardData(cardData);
        }
        setCardErrors(errors); 
        if (errors.valid) {
            setOpen(true);
            const email = localStorage.getItem("email");
            const token = localStorage.getItem("token");

            const idPlano = plano.id;
            const tituloPlano = plano.title;
            const periodicity = plano.periodicity;
            cardData.number = TirarMascara(cardData.number);

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/criarAssinatura`, {
                idDesconto: discountApplied ? discountApplied.idDesconto : false,
                email: email,
                valor: total,
                metodo: paymentMethod,
                idPlano: idPlano,
                titulo: tituloPlano,
                periodicidade: periodicity,
                descricao: plano.description,
                cardData: paymentMethod === "CREDIT_CARD" ? cardData : null
            }, {
                headers: { Authorization: token }
            });

            if (response.data.status === 200) {
                setOpen(false);
                await fetchUser();
                navigate("/apresentacao-clarea");
            } else {
                setOpen(false);
                setMensagem("Erro ao processar pagamento. Tente novamente.");
                setOpenSnackbarError(true);
            }
        }
    };

    const applyDiscount = async () => {

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/aplicarDesconto`, {
            codigo: discountCode
        }, {
            headers: { Authorization: token },
        });

        if (response.data.status === 200) {
            setDiscountApplied(response.data.desconto);
            setMensagem("Desconto aplicado com sucesso!");
            setOpenSnackbarSuccess(true);
            return;
        } else {
            setMensagem("Cupom inválido.");
            setOpenSnackbarError(true);
        }
    };

    const handleCardInputChange = (e) => {
        setCardData({ ...cardData, [e.target.name]: e.target.value });
    };

    // const handleCheck = (e) => {
    //     setIsChecked(e.target.checked);
    // }

    const cardStyle = {
        backgroundColor: "#f9f9f9",
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        padding: isMobile ? "16px" : "24px 28px",
        boxSizing: "border-box",
    };

    const sectionTitleStyle = {
        margin: "0 0 16px",
        fontSize: isMobile ? "1.1rem" : "1.25rem",
        color: "#0b243d",
        fontWeight: 700,
    };

    const paymentOptions = [
        { value: "BOLETO", label: "Boleto Bancário", description: "Compensação em até 3 dias úteis", icon: ReceiptLongOutlinedIcon },
        { value: "CREDIT_CARD", label: "Cartão de Crédito", description: "Aprovação imediata", icon: CreditCardOutlinedIcon },
    ];

    return (
        <>
            <Header active="checkout" />
            <div style={{ marginTop: "80px", width: "100%", padding: "20px", minHeight: "100vh", boxSizing: "border-box" }}>
                <div style={{
                    width: isMobile ? "100%" : "80%",
                    margin: "0 auto",
                    padding: isMobile ? "10px" : "20px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "12px",
                    boxSizing: "border-box",
                }}>
                    <div style={{ padding: isMobile ? "6px 4px 16px" : "4px 4px 20px" }}>
                        <h1 style={{ margin: 0, fontSize: isMobile ? "1.5rem" : "2rem", color: "#0b243d", fontWeight: 700, lineHeight: 1.25 }}>
                            Finalizar assinatura
                        </h1>
                        <p style={{ margin: "8px 0 0", fontSize: isMobile ? "0.9rem" : "1rem", color: "#555", lineHeight: 1.5 }}>
                            Escolha a forma de pagamento e confirme o seu pedido.
                        </p>
                    </div>

                    <div style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: "flex-start",
                        gap: "20px",
                    }}>
                        {/* Pagamento */}
                        <div style={{ ...cardStyle, flex: 1, width: isMobile ? "100%" : "auto", minWidth: 0 }}>
                            <h2 style={sectionTitleStyle}>Forma de Pagamento</h2>

                            <FormControl component="fieldset" sx={{ width: "100%" }}>
                                <RadioGroup
                                    value={paymentMethod}
                                    onChange={handlePaymentChange}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                        gap: "12px",
                                    }}
                                >
                                    {paymentOptions.map((option) => {
                                        const selected = paymentMethod === option.value;
                                        const Icon = option.icon;
                                        return (
                                            <FormControlLabel
                                                key={option.value}
                                                value={option.value}
                                                control={<Radio sx={{ color: "#8a97a5", "&.Mui-checked": { color: "#0b243d" } }} />}
                                                label={
                                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                        <Icon sx={{ color: selected ? "#0b243d" : "#8a97a5", fontSize: 28 }} />
                                                        <div>
                                                            <p style={{ margin: 0, fontWeight: 700, color: "#0b243d", fontSize: "0.95rem" }}>
                                                                {option.label}
                                                            </p>
                                                            <p style={{ margin: 0, color: "#555", fontSize: "0.8rem" }}>
                                                                {option.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                }
                                                sx={{
                                                    m: 0,
                                                    p: "12px 14px",
                                                    backgroundColor: selected ? "rgba(255, 200, 69, 0.12)" : "white",
                                                    border: selected ? "2px solid #0b243d" : "2px solid #e0e0e0",
                                                    borderRadius: "12px",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": { borderColor: "#0b243d" },
                                                }}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormControl>

                            {paymentMethod === "BOLETO" && (
                                <div style={{
                                    marginTop: "20px",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "10px",
                                    padding: "14px 16px",
                                    backgroundColor: "white",
                                    border: "1px dashed #d0d6dd",
                                    borderRadius: "12px",
                                }}>
                                    <InfoOutlinedIcon sx={{ color: "#0b243d", fontSize: 20, mt: "1px" }} />
                                    <Typography variant="body2" sx={{ color: "#555" }}>
                                        O boleto será gerado após a confirmação do pedido, com vencimento apenas no próximo mês.
                                    </Typography>
                                </div>
                            )}

                        {paymentMethod === "CREDIT_CARD" && (
                            <div
                                style={{
                                    marginTop: "24px",
                                    display: "flex",
                                    flexDirection: isMobile ? "column" : "row",
                                    alignItems: isMobile ? "stretch" : "center",
                                    gap: "24px",
                                }}
                            >
                                {/* Formulário */}
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "16px",
                                    }}
                                >
                                    <TextField
                                        label="Nome no Cartão"
                                        fullWidth
                                        size="small"
                                        placeholder="Ex: João da Silva"
                                        name="name"
                                        value={cardData.name}
                                        onChange={handleCardInputChange}
                                        helperText={cardErrors.name}
                                        error={Boolean(cardErrors.name)}
                                        sx={fieldSx}
                                    />

                                    <TextField
                                        label="Número do Cartão"
                                        fullWidth
                                        size="small"
                                        inputProps={{ maxLength: 19, inputMode: "numeric" }}
                                        placeholder="0000 0000 0000 0000"
                                        name="number"
                                        value={MascaraCartaoCredito(cardData.number)}
                                        onChange={handleCardInputChange}
                                        helperText={cardErrors.number}
                                        error={Boolean(cardErrors.number)}
                                        sx={fieldSx}
                                    />

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "16px",
                                        }}
                                    >
                                        <TextField
                                            label="Validade"
                                            size="small"
                                            placeholder="MM/AAAA"
                                            inputProps={{ maxLength: 7, inputMode: "numeric" }}
                                            name="expiry"
                                            value={MascaraDataCartaoCredito(cardData.expiry)}
                                            onChange={handleCardInputChange}
                                            fullWidth
                                            helperText={cardErrors.expiry}
                                            error={Boolean(cardErrors.expiry)}
                                            sx={fieldSx}
                                        />

                                        <TextField
                                            label="CVV"
                                            size="small"
                                            placeholder="123"
                                            inputProps={{ maxLength: 3, inputMode: "numeric" }}
                                            name="cvv"
                                            value={cardData.cvv}
                                            onChange={handleCardInputChange}
                                            onFocus={() => setShowBack(true)}  // mostra verso
                                            onBlur={() => setShowBack(false)}  // volta para frente
                                            fullWidth
                                            helperText={cardErrors.cvv}
                                            error={Boolean(cardErrors.cvv)}
                                            sx={fieldSx}
                                        />
                                    </div>

                                    {/* <FormControlLabel
                                        sx={{ width: "fit-content" }}
                                        control={<Checkbox />}
                                        label={
                                            <span style={{ fontSize: "0.9rem", userSelect: "none" }}>
                                                O titular do cartão é diferente do cadastro?
                                            </span>
                                        }
                                    /> */}
                                </div>

                                {/* Cartão com efeito flip */}
                                <div
                                    style={{
                                        flex: 1,
                                        perspective: "1000px",
                                        maxWidth: "340px",
                                        width: "100%",
                                        aspectRatio: "1.586",
                                        display: isMobile ? "none" : "block",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "14px",
                                            boxShadow: "0 10px 24px rgba(11, 36, 61, 0.35)",
                                            color: "white",
                                            fontFamily: "monospace",
                                            transition: "transform 0.6s",
                                            transformStyle: "preserve-3d",
                                            transform: showBack ? "rotateY(180deg)" : "rotateY(0deg)",
                                            position: "relative",
                                        }}
                                    >
                                        {/* Frente do cartão */}
                                        <div
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                boxSizing: "border-box",
                                                backfaceVisibility: "hidden",
                                                background: "linear-gradient(135deg, #0b243d 0%, #1d4268 100%)",
                                                borderRadius: "14px",
                                                padding: "20px 22px",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {/* Detalhe decorativo */}
                                            <div style={{
                                                position: "absolute",
                                                top: "-50px",
                                                right: "-50px",
                                                width: "140px",
                                                height: "140px",
                                                borderRadius: "50%",
                                                border: "26px solid #ffc845",
                                                opacity: 0.35,
                                            }} />
                                            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span style={{ fontSize: "0.75rem", letterSpacing: "1px", opacity: 0.85 }}>CARTÃO DE CRÉDITO</span>
                                            </div>
                                            <div style={{
                                                width: "40px",
                                                height: "30px",
                                                borderRadius: "6px",
                                                background: "linear-gradient(135deg, #ffd978, #e9b53d)",
                                            }} />
                                            <div style={{ position: "relative", fontSize: "1.2rem", letterSpacing: "2px" }}>
                                                {MascaraCartaoCredito(cardData.number) || "0000 0000 0000 0000"}
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: "10px",
                                                    fontSize: "0.85rem",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {cardData.name || "NOME DO TITULAR"}
                                                </span>
                                                <span>{MascaraDataCartaoCredito(cardData.expiry) || "MM/AAAA"}</span>
                                            </div>
                                        </div>

                                        {/* Verso do cartão */}
                                        <div
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                boxSizing: "border-box",
                                                backfaceVisibility: "hidden",
                                                background: "linear-gradient(135deg, #1d4268 0%, #0b243d 100%)",
                                                borderRadius: "14px",
                                                padding: "20px 0",
                                                transform: "rotateY(180deg)",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    background: "#000",
                                                    height: "40px",
                                                    marginBottom: "20px",
                                                }}
                                            />
                                            <div
                                                style={{
                                                    alignSelf: "flex-end",
                                                    marginRight: "22px",
                                                    background: "#fff",
                                                    color: "#000",
                                                    padding: "5px 10px",
                                                    borderRadius: "6px",
                                                    fontWeight: "bold",
                                                    minWidth: "60px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                {cardData.cvv || "•••"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* {isChecked && (
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "15px", marginTop: "20px" }}>
                                <TextField
                                    label="E-mail do Titular"
                                    fullWidth
                                    size="small"
                                    placeholder="exemplo@gmail.com"
                                    name="email"
                                />
                                <div style={{
                                    display: "flex",
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: "15px"
                                }}>
                                    <TextField
                                        label="CPF ou CNPJ do Titular"
                                        size="small"
                                        fullWidth
                                        placeholder="xxx.xxx.xxx-xx ou xx.xxx.xxx/xxxx-xx"
                                        inputProps={{ maxLength: 18 }}
                                        name="document"
                                    />
                                    <TextField
                                        label="CEP do Titular"
                                        size="small"
                                        fullWidth
                                        placeholder="xxxxx-xxx"
                                        inputProps={{ maxLength: 10 }}
                                        name="cep"
                                    />
                                    <TextField
                                        label="Telefone do Titular"
                                        size="small"
                                        fullWidth
                                        placeholder="(xx) xxxxx-xxxx"
                                        inputProps={{ maxLength: 15 }}
                                        name="phone"
                                    />
                                </div>
                            </div>
                        )} */}
                        </div>

                        {/* Resumo do pedido */}
                        <div style={{
                            ...cardStyle,
                            width: isMobile ? "100%" : "360px",
                            flexShrink: 0,
                            position: isMobile ? "static" : "sticky",
                            top: "100px",
                        }}>
                            <h2 style={sectionTitleStyle}>Resumo do Pedido</h2>

                            {cartItems.map(item => (
                                <div key={item.id} style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    padding: "14px 16px",
                                    backgroundColor: "white",
                                    border: "1px solid #e0e0e0",
                                    borderLeft: "5px solid #ffc845",
                                    borderRadius: "10px",
                                }}>
                                    <div style={{ minWidth: 0 }}>
                                        <p style={{ margin: 0, fontSize: "0.75rem", color: "#8a97a5", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                            Plano
                                        </p>
                                        <p style={{ margin: "2px 0 0", fontWeight: 700, color: "#0b243d" }}>{item.title}</p>
                                        {item.periodicity && (
                                            <p style={{ margin: "2px 0 0", fontSize: "0.85rem", color: "#555" }}>Plano {item.periodicity}</p>
                                        )}
                                    </div>
                                    <p style={{ margin: 0, whiteSpace: "nowrap", color: "#0b243d", fontWeight: 600 }}>
                                        {FormatToBrl(item.price)}<span style={{ fontSize: "0.8rem", color: "#555", fontWeight: 400 }}>/mês</span>
                                    </p>
                                </div>
                            ))}

                            {/* Cupom de desconto */}
                            <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                                <TextField
                                    disabled={discountApplied !== false}
                                    label="Cupom de desconto"
                                    autoComplete="off"
                                    size="small"
                                    fullWidth
                                    value={discountCode}
                                    onChange={(e) => setDiscountCode(e.target.value)}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: "#8a97a5" }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={applyDiscount}
                                    disabled={discountApplied !== false}
                                    startIcon={discountApplied ? <CheckCircleIcon /> : null}
                                    sx={{
                                        flexShrink: 0,
                                        height: "40px",
                                        px: "18px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        color: "#0b243d",
                                        borderColor: "#0b243d",
                                        borderRadius: "10px",
                                        "&:hover": { borderColor: "#0b243d", backgroundColor: "rgba(11, 36, 61, 0.06)" },
                                        "&.Mui-disabled": discountApplied
                                            ? { color: "#2e7d32", borderColor: "#8CD790", backgroundColor: "rgba(140, 215, 144, 0.15)" }
                                            : {},
                                    }}
                                >
                                    {discountApplied ? "Aplicado" : "Aplicar"}
                                </Button>
                            </div>

                            {/* Valores */}
                            <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem", color: "#555" }}>
                                    <span>Subtotal</span>
                                    <span>{FormatToBrl(originalTotal)}</span>
                                </div>
                                {discountApplied && (
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem", color: "#2e7d32", fontWeight: 600 }}>
                                        <span>Desconto ({Number(discountApplied.valorDesconto)}%)</span>
                                        <span>- {FormatToBrl(cartItems[0]?.price * (Number(discountApplied.valorDesconto) / 100))}</span>
                                    </div>
                                )}
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    paddingTop: "12px",
                                    borderTop: "1px solid #e0e0e0",
                                }}>
                                    <span style={{ fontWeight: 700, color: "#0b243d" }}>Total</span>
                                    <span style={{ fontWeight: 700, color: "#0b243d", fontSize: isMobile ? "1.4rem" : "1.6rem" }}>
                                        {FormatToBrl(total)}
                                    </span>
                                </div>
                            </div>

                            {/* Observações da contratação */}
                            <div style={{
                                marginTop: "20px",
                                padding: "14px 16px",
                                backgroundColor: "rgba(140, 215, 144, 0.15)",
                                border: "1px solid rgba(140, 215, 144, 0.6)",
                                borderRadius: "10px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                            }}>
                                {[
                                    <>O pagamento é cobrado <strong>somente no próximo mês</strong>.</>,
                                    <><strong>Acesso total</strong> à plataforma e suporte da nossa equipe já no ato da contratação.</>,
                                    <>Cancele quando quiser, <strong>sem multa e sem fidelidade</strong>.</>,
                                ].map((texto, index) => (
                                    <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18, mt: "1px", flexShrink: 0 }} />
                                        <span style={{ fontSize: "0.85rem", color: "#0b243d", lineHeight: 1.4 }}>{texto}</span>
                                    </div>
                                ))}
                            </div>

                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleFinishOrder}
                                startIcon={<LockOutlinedIcon />}
                                sx={{
                                    mt: "20px",
                                    padding: "12px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    backgroundColor: "#ffc845",
                                    color: "#0b243d",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 10px rgba(255, 200, 69, 0.4)",
                                    transition: "0.3s ease",
                                    '&:hover': {
                                        backgroundColor: "#e9b53d",
                                        boxShadow: "0 6px 14px rgba(255, 200, 69, 0.5)",
                                    },
                                }}
                            >
                                Confirmar Pedido
                            </Button>

                            <Typography variant="body2" sx={{ mt: "14px", color: "#8a97a5", fontSize: "0.8rem", lineHeight: 1.5, textAlign: "center" }}>
                                Seus dados serão utilizados para processar a compra, melhorar sua experiência e conforme nossa{" "}
                                <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" style={{ color: "#0b243d", fontWeight: 600, textDecoration: "underline" }}>
                                    política de privacidade
                                </a>.
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <Loading open={open} />
            <AlertError openSnackbarError={openSnackbarError} setOpenSnackbarError={setOpenSnackbarError} mensagem={mensagem} />
            <AlertSuccess openSnackbarSuccess={openSnackbarSuccess} setOpenSnackbarSuccess={setOpenSnackbarSuccess} mensagem={mensagem} />
        </>
    );
};

export default Checkout;
